import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { styled } from '@mui/system';
import FAIcon from '#/common/components/FAIcon';
import { color } from '@nuvolo/nuux-theme/utils';
import If from '#/common/components/If';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';

const CardContainer = styled('div')<{ isDesktop?: boolean }>(({ isDesktop }) => ({
  display: `${isDesktop ? 'flex' : 'grid'}`,
  gap: '20px',
  justifyContent: 'center',
}));

const OptionCard = styled('div')<{ selected?: boolean }>(({ selected }) => ({
  width: '250px',
  height: '100px',
  border: `2px solid ${selected ? color('primary', 'dark') : color('primary', 'light3')}`,
  borderRadius: '12px',
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: `${selected ? color('custom', 'contrastText') : 'transparent'}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: `${selected ? color('primary', 'dark') : color('primary', 'light3')}`,
    backgroundColor: `${selected ? color('custom', 'contrastText') : color('primary', 'light3')}`,
  },
}));

const IconWrapper = styled('div')<{ selected?: boolean }>(({ selected }) => ({
  marginBottom: '5px',
  color: selected ? color('custom', 'invertedBackground') : color('custom', 'contrastText'),
}));

const CardTitle = styled(NuvoText)<{ selected?: boolean }>(({ selected }) => ({
  textAlign: 'center',
  fontWeight: '500',
  width: '50%',
  color: selected ? color('custom', 'invertedBackground') : color('custom', 'contrastText'),
}));

export interface OptionCardSelectorProps {
  onAction: () => void;
  onClose: () => void;
  actionText: string;
  newText?: string;
  actionIcon: string;
  newIcon?: string;
  loading?: boolean;
}

type CardOption = 'action' | 'new' | null;

const OptionCardSelector: React.FC<OptionCardSelectorProps> = (props) => {
  const { onAction, onClose, actionText, newText, actionIcon, newIcon, loading } = props;

  const [selectedOption, setSelectedOption] = useState<CardOption>(null);
  const navigate = useNavigate();
  const { isDesktop } = useViewport();

  const handleNext = useCallback(() => {
    if (selectedOption === 'action') {
      onAction();
    } else if (selectedOption === 'new') {
      onClose();
    }
  }, [selectedOption, onAction, onClose]);

  const cancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <>
      <If condition={loading}>
        <StandardThrobber />
      </If>
      <If condition={!loading}>
        <Modal
          visible={!loading}
          title="How do you want to start?"
          dragEnabled={true}
          width={550}
          onHiding={cancel}
          content={
            <CardContainer isDesktop={isDesktop}>
              <OptionCard
                selected={selectedOption === 'action'}
                onClick={() => setSelectedOption('action')}>
                <IconWrapper selected={selectedOption === 'action'}>
                  <FAIcon icon={actionIcon} />
                </IconWrapper>
                <CardTitle selected={selectedOption === 'action'} type="body">
                  {actionText}
                </CardTitle>
              </OptionCard>

              <OptionCard
                selected={selectedOption === 'new'}
                onClick={() => setSelectedOption('new')}>
                <IconWrapper selected={selectedOption === 'new'}>
                  <FAIcon icon={newIcon || 'pen'} />
                </IconWrapper>
                <CardTitle type="body" selected={selectedOption === 'new'}>
                  {newText || 'Start from Scratch'}
                </CardTitle>
              </OptionCard>
            </CardContainer>
          }
          actions={[
            <TMMButton key="cancel" label="Cancel" type="normal" onClick={cancel} />,
            <TMMButton
              key="ok"
              label="Next"
              type="submit"
              disabled={!selectedOption}
              onClick={handleNext}
            />,
          ]}
        />
      </If>
    </>
  );
};

export default React.memo(OptionCardSelector);
